/* ============================================================
   KKUMI — DECO STYLE FINDER
   3-question quiz (mood -> density -> skill) that matches a
   Colour Pop Kit and paints a randomised starter sticker layout
   on a toploader. Renders into [data-deco-finder].

   The question flow, kit copy and sticker palette are interaction
   content and live here. The merchant-editable bits arrive on the
   host as data attributes: data-shop (a JSON map keyed by vibe ->
   kit shop URL, plus "default") and data-cta (the result button
   label). Built with innerHTML from trusted constants only; the
   one settings value (a shop URL) is applied via setAttribute.
   ============================================================ */
(function () {
  'use strict';

  // Escape merchant-editable copy before it lands in innerHTML. Prefers the
  // shared KKEsc primitive; falls back to a tiny inline escaper if it's absent.
  var esc = window.KKEsc || function (v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  /* ---- sticker DOM builder (CSS shapes, no images) ---- */
  function makeSticker(spec) {
    var s = document.createElement('div');
    s.className = 'dk-st dk-st--' + spec.kind;
    s.style.width = spec.w + 'px';
    s.style.height = (spec.h || spec.w) + 'px';
    if (spec.color) s.style.setProperty('--c', spec.color);
    if (spec.kind === 'tile') {
      var span = document.createElement('span');
      span.textContent = spec.label || 'K';
      s.appendChild(span);
    }
    if (spec.kind === 'bow') s.appendChild(document.createElement('i'));
    s.dataset.rot = (spec.rot != null ? spec.rot : 0);
    s.dataset.scale = (spec.scale != null ? spec.scale : 1);
    return s;
  }
  function applyT(s) {
    s.style.transform = 'translate(-50%,-50%) rotate(' + (s.dataset.rot || 0) + 'deg) scale(' + (s.dataset.scale || 1) + ')';
  }
  function loaderHTML(label) {
    return '' +
      '<div class="dk-loader" data-loader>' +
        '<div class="dk-loader__card"><span class="dk-loader__lab">' + label + '</span></div>' +
        '<span class="dk-loader__gloss"></span>' +
      '</div>';
  }

  var QUESTIONS = [
    { key: 'vibe', q: 'what mood are you going for?', hint: 'This sets your colour story.',
      opts: [
        { v: 'soft', t: 'Soft & dreamy', d: 'Pastel pinks, whites, gentle' },
        { v: 'bold', t: 'Bold & poppy', d: 'Hot pink, red, high energy' },
        { v: 'cool', t: 'Cool & calm', d: 'Mint, lavender, airy' },
        { v: 'mono', t: 'Mono-chic', d: 'Ink, pearl, restrained' }
      ] },
    { key: 'density', q: 'how full do you like it?', hint: 'How much of the card gets covered.',
      opts: [
        { v: 'min', t: 'Minimal', d: 'A few intentional pieces' },
        { v: 'bal', t: 'Balanced', d: 'Framed, breathing room' },
        { v: 'max', t: 'Maximal', d: 'Edge to edge, packed' }
      ] },
    { key: 'skill', q: "what's your deco experience?", hint: 'We tune the starting point to you.',
      opts: [
        { v: 'new', t: 'First deco', d: 'Brand new to this' },
        { v: 'few', t: 'Done a few', d: 'Getting the hang of it' },
        { v: 'pro', t: 'Seasoned', d: 'I do this a lot' }
      ] }
  ];

  var KITS = {
    soft: { name: 'Cloud Nine', series: 'Colour Pop Kit · Soft', bg: 'linear-gradient(160deg,#ffe6f1,#ffd0e6)',
      pal: [{ kind: 'bow', color: '#f279a1' }, { kind: 'heart', color: '#FFC9E0' }, { kind: 'pearl', color: '#ffffff' }, { kind: 'sparkle', color: '#ffffff' }, { kind: 'dot', color: '#f279a1' }, { kind: 'star', color: '#f5d99b' }],
      chips: ['12 pastel sticker sheets', '2 bow + ribbon sheets', 'pearl + sparkle minis', 'colour-matched filler'],
      blurb: 'Soft pinks, creamy whites and just enough sparkle. The gentlest place to start.' },
    bold: { name: 'Sugar Rush', series: 'Colour Pop Kit · Bold', bg: 'linear-gradient(160deg,#ffd0e6,#ff9ec4)',
      pal: [{ kind: 'bow', color: '#E0277F' }, { kind: 'heart', color: '#FF3D9A' }, { kind: 'star', color: '#f279a1' }, { kind: 'sparkle', color: '#FF3D9A' }, { kind: 'tile', color: '#E0277F', label: '♡' }, { kind: 'dot', color: '#E0277F' }],
      chips: ['14 high-pop sheets', 'jumbo anchor stickers', 'letter + emoticon sheet', 'red + hot-pink hearts'],
      blurb: 'Maximum saturation — hot pinks, reds and chunky anchors for a look that shouts.' },
    cool: { name: 'Mint Condition', series: 'Colour Pop Kit · Cool', bg: 'linear-gradient(160deg,#dff3e8,#dfe2fb)',
      pal: [{ kind: 'bow', color: '#bfe3cf' }, { kind: 'star', color: '#a9c8f0' }, { kind: 'heart', color: '#c9b8f0' }, { kind: 'pearl', color: '#ffffff' }, { kind: 'sparkle', color: '#bfe3cf' }, { kind: 'dot', color: '#6652b5' }],
      chips: ['10 cool-tone sheets', 'mint + lavender ribbon', 'frosted pearl minis', 'airy filler set'],
      blurb: 'Mints and lavenders for a calm, frosted finish. Cool-toned cards, meet your match.' },
    mono: { name: 'Ink & Pearl', series: 'Colour Pop Kit · Mono', bg: 'linear-gradient(160deg,#f3eef1,#e4dbe4)',
      pal: [{ kind: 'bow', color: '#3d1429' }, { kind: 'pearl', color: '#ffffff' }, { kind: 'tile', color: '#3d1429', label: 'K' }, { kind: 'dot', color: '#3d1429' }, { kind: 'sparkle', color: '#ffffff' }, { kind: 'heart', color: '#8b7886' }],
      chips: ['8 mono sheets', 'ink + pearl ribbon', 'monogram letter tiles', 'gloss-black minis'],
      blurb: 'Ink, pearl and restraint. For the collector who likes it sharp and grown-up.' }
  };
  var COUNTS = { min: 5, bal: 8, max: 13 };

  var IC = {
    star: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 2 2.4 7.4H22l-6 4.6 2.3 7.4L12 17l-6.3 4.4L8 14 2 9.4h7.6z"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>',
    back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>',
    restart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><path d="M21 3v5h-5"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/></svg>'
  };

  function genLayout(vibe, density) {
    var kit = KITS[vibe];
    var n = COUNTS[density];
    var out = [];
    var anchor = kit.pal[0];
    out.push({ kind: anchor.kind, color: anchor.color, label: anchor.label, w: 56, h: anchor.kind === 'bow' ? 40 : 50, x: 0.5, y: 0.16, rot: -6 });
    var placed = 0, guard = 0;
    while (placed < n - 1 && guard < 200) {
      guard++;
      var sp = kit.pal[1 + Math.floor(Math.random() * (kit.pal.length - 1))];
      var x = 0.1 + Math.random() * 0.8;
      var y = 0.24 + Math.random() * 0.66;
      var inFace = x > 0.3 && x < 0.7 && y > 0.34 && y < 0.62;
      if (inFace && density !== 'max' && Math.random() < 0.8) continue;
      var big = sp.kind === 'bow' || sp.kind === 'star';
      out.push({ kind: sp.kind, color: sp.color, label: sp.label,
        w: big ? 30 : (sp.kind === 'pearl' || sp.kind === 'dot' ? 13 : 24),
        h: big ? 28 : (sp.kind === 'pearl' || sp.kind === 'dot' ? 13 : 22),
        x: x, y: y, rot: Math.random() * 30 - 15 });
      placed++;
    }
    return out;
  }

  function initFinder(root) {
    if (root.dataset.kkBound) return;
    root.dataset.kkBound = '1';

    var shop = {};
    try { shop = JSON.parse(root.dataset.shop || '{}'); } catch (e) { shop = {}; }
    var ctaLabel = root.dataset.cta || 'Shop the {kit} kit';
    var loaderLabel = root.dataset.loaderLabel || 'your bias here';

    // Merchant-editable chrome (data-* on the host); each falls back to its literal.
    var brandLabel = root.dataset.brand || 'Style Finder';
    var taglineLabel = root.dataset.tagline || '3 taps to your kit';
    var counterTpl = root.dataset.qCounter || 'question [n] / [total]';
    var backLabel = root.dataset.backLabel || 'back';
    var reshuffleLabel = root.dataset.reshuffleLabel || 'reshuffle layout';
    var matchLabel = root.dataset.matchLabel || 'your match';
    var restartLabel = root.dataset.restartLabel || 'start over';
    var skillNotes = {
      new: root.dataset.skillNew || 'Beginner-friendly — pre-matched so nothing clashes.',
      few: root.dataset.skillFew || 'A comfortable next step with room to experiment.',
      pro: root.dataset.skillPro || 'Plenty to layer and remix for advanced looks.'
    };

    // Optional question/kit copy overrides (JSON). Blank/invalid = built-in.
    // Kit overrides only touch copy fields (name/series/blurb/chips); the palette,
    // gradient and counts stay code-owned so the sticker render can't break.
    var Q = QUESTIONS;
    try { var qj = JSON.parse(root.dataset.questions || 'null'); if (qj && qj.length) Q = qj; } catch (e) {}
    var KITSET = KITS;
    try {
      var kj = JSON.parse(root.dataset.kits || 'null');
      if (kj && typeof kj === 'object') {
        KITSET = {};
        Object.keys(KITS).forEach(function (k) {
          var base = KITS[k], ov = kj[k] || {};
          KITSET[k] = {
            name: ov.name != null ? ov.name : base.name,
            series: ov.series != null ? ov.series : base.series,
            blurb: ov.blurb != null ? ov.blurb : base.blurb,
            chips: (ov.chips && ov.chips.length) ? ov.chips : base.chips,
            bg: base.bg, pal: base.pal
          };
        });
      }
    } catch (e) {}

    var pipsHtml = '';
    for (var pi = 0; pi < Q.length; pi++) pipsHtml += '<i data-fpip>' + (pi + 1) + '</i>';
    root.innerHTML =
      '<div class="dk-finder__top">' +
        '<div class="dk-finder__brand"><i>' + IC.star + '</i><div><b>' + esc(brandLabel) + '</b><span>' + esc(taglineLabel) + '</span></div></div>' +
        '<div class="dk-finder__pips">' + pipsHtml + '</div>' +
      '</div>' +
      '<div class="dk-finder__body"><div data-fstep></div><div data-fresult></div></div>';

    var stepWrap = root.querySelector('[data-fstep]');
    var result = root.querySelector('[data-fresult]');
    var pips = root.querySelectorAll('[data-fpip]');
    var answers = {};
    var step = 0;

    function renderPips() {
      pips.forEach(function (p, i) {
        p.classList.toggle('is-done', i < step);
        p.classList.toggle('is-active', i === step && step < Q.length);
      });
    }

    function renderStep() {
      var qStep = Q[step];
      result.classList.remove('is-on');
      stepWrap.style.display = '';
      stepWrap.innerHTML =
        '<p class="dk-fq__n">' + esc(counterTpl.split('[n]').join(step + 1).split('[total]').join(Q.length)) + '</p>' +
        '<h3 class="dk-fq__q">' + esc(qStep.q) + '</h3>' +
        '<p class="dk-fq__hint">' + esc(qStep.hint) + '</p>' +
        '<div class="dk-fq__opts dk-fq__opts--' + qStep.opts.length + '">' +
          qStep.opts.map(function (o) {
            return '<button type="button" class="dk-opt" data-v="' + esc(o.v) + '"><b>' + esc(o.t) + '</b><span>' + esc(o.d) + '</span>' +
              '<i class="dk-opt__go">' + IC.arrow + '</i></button>';
          }).join('') +
        '</div>' +
        (step > 0 ? '<button type="button" class="dk-fback" data-fback>' + IC.back + ' ' + esc(backLabel) + '</button>' : '');
      stepWrap.classList.remove('dk-fade'); void stepWrap.offsetWidth; stepWrap.classList.add('dk-fade');
      stepWrap.querySelectorAll('.dk-opt').forEach(function (b) {
        b.addEventListener('click', function () {
          answers[Q[step].key] = b.dataset.v;
          step++;
          renderPips();
          if (step >= Q.length) renderResult(); else renderStep();
        });
      });
      var back = stepWrap.querySelector('[data-fback]');
      if (back) back.addEventListener('click', function () { step--; renderPips(); renderStep(); });
      renderPips();
    }

    function renderResult() {
      var kit = KITSET[answers.vibe];
      stepWrap.style.display = 'none';
      result.classList.add('is-on');
      var skillNote = skillNotes[answers.skill] || '';

      result.innerHTML =
        '<div class="dk-res">' +
          '<div class="dk-res__preview" style="--rbg:' + kit.bg + '">' +
            '<div class="dk-st-stage" data-rstage>' + loaderHTML(esc(loaderLabel)) + '</div>' +
            '<button type="button" class="dk-res__regen" data-fregen>' + IC.restart + ' ' + esc(reshuffleLabel) + '</button>' +
          '</div>' +
          '<div class="dk-res__body">' +
            '<div class="dk-res__badge">' + IC.star + ' ' + esc(matchLabel) + '</div>' +
            '<p class="dk-res__series">' + esc(kit.series) + '</p>' +
            '<h3 class="dk-res__name">' + esc(kit.name) + '</h3>' +
            '<p class="dk-res__blurb">' + esc(kit.blurb) + '</p>' +
            '<div class="dk-res__chips">' + kit.chips.map(function (c) { return '<span class="dk-res__chip">' + esc(c) + '</span>'; }).join('') + '</div>' +
            '<p class="dk-res__note">' + IC.info + ' ' + esc(skillNote) + '</p>' +
            '<div class="dk-res__actions">' +
              '<a class="button" data-fshop></a>' +
              '<button type="button" class="dk-restart" data-frestart>' + IC.restart + ' ' + esc(restartLabel) + '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
      result.classList.remove('dk-fade'); void result.offsetWidth; result.classList.add('dk-fade');

      var shopBtn = result.querySelector('[data-fshop]');
      shopBtn.textContent = ctaLabel.replace('{kit}', kit.name);
      var href = shop[answers.vibe] || shop['default'] || '';
      if (href) shopBtn.setAttribute('href', href);

      function paint() {
        var stg = result.querySelector('[data-rstage]');
        stg.querySelectorAll('.dk-st').forEach(function (n) { n.remove(); });
        genLayout(answers.vibe, answers.density).forEach(function (sp, i) {
          var s = makeSticker(sp);
          s.style.left = (sp.x * 100) + '%';
          s.style.top = (sp.y * 100) + '%';
          s.dataset.rot = sp.rot;
          applyT(s);
          s.style.animationDelay = (i * 45) + 'ms';
          s.classList.add('dk-pop');
          stg.appendChild(s);
        });
      }
      requestAnimationFrame(paint);
      result.querySelector('[data-fregen]').addEventListener('click', paint);
      result.querySelector('[data-frestart]').addEventListener('click', function () {
        step = 0;
        for (var k in answers) delete answers[k];
        renderPips();
        renderStep();
      });
    }

    renderStep();
  }

  function boot() { document.querySelectorAll('[data-deco-finder]').forEach(initFinder); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
